/**
 * Distance Calculator Utilities
 * Haversine formula and ETA calculations
 */

import { DEFAULT_SPEED_KMH, METERS_PER_KM, SECONDS_PER_MINUTE } from '../core/constants';

/**
 * Calculate straight-line distance between two points using Haversine formula
 * @param {Object} point1 - { lat, lng }
 * @param {Object} point2 - { lat, lng }
 * @returns {number} Distance in kilometers
 */
export function calculateDistance(point1, point2) {
  const R = 6371; // Earth's radius in kilometers
  const dLat = toRadians(point2.lat - point1.lat);
  const dLon = toRadians(point2.lng - point1.lng);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(point1.lat)) *
      Math.cos(toRadians(point2.lat)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Calculate distance in meters
 * @param {Object} point1 - { lat, lng }
 * @param {Object} point2 - { lat, lng }
 * @returns {number} Distance in meters
 */
export function calculateDistanceInMeters(point1, point2) {
  return calculateDistance(point1, point2) * METERS_PER_KM;
}

/**
 * Calculate ETA based on distance and speed
 * @param {number} distanceKm - Distance in kilometers
 * @param {number} speedKmh - Speed in km/h (default: 30)
 * @returns {number} ETA in minutes
 */
export function calculateETA(distanceKm, speedKmh = DEFAULT_SPEED_KMH) {
  if (speedKmh <= 0) return null;
  return (distanceKm / speedKmh) * 60;
}

/**
 * Calculate ETA in seconds
 * @param {number} distanceKm - Distance in kilometers
 * @param {number} speedKmh - Speed in km/h
 * @returns {number} ETA in seconds
 */
export function calculateETAInSeconds(distanceKm, speedKmh = DEFAULT_SPEED_KMH) {
  if (speedKmh <= 0) return null;
  return (distanceKm / speedKmh) * 3600;
}

/**
 * Format distance for display
 * @param {number} distanceKm - Distance in kilometers
 * @returns {string} Formatted distance (e.g., "2.5 km" or "500 m")
 */
export function formatDistance(distanceKm) {
  if (distanceKm < 1) {
    return `${Math.round(distanceKm * 1000)} m`;
  }
  return `${distanceKm.toFixed(1)} km`;
}

/**
 * Format duration for display
 * @param {number} durationMinutes - Duration in minutes
 * @returns {string} Formatted duration (e.g., "25 mins" or "1 hr 15 mins")
 */
export function formatDuration(durationMinutes) {
  if (durationMinutes < 60) {
    return `${Math.round(durationMinutes)} min${durationMinutes !== 1 ? 's' : ''}`;
  }

  const hours = Math.floor(durationMinutes / 60);
  const minutes = Math.round(durationMinutes % 60);

  if (minutes === 0) {
    return `${hours} hr${hours !== 1 ? 's' : ''}`;
  }

  return `${hours} hr${hours !== 1 ? 's' : ''} ${minutes} min${minutes !== 1 ? 's' : ''}`;
}

/**
 * Convert degrees to radians
 * @private
 * @param {number} degrees
 * @returns {number} Radians
 */
function toRadians(degrees) {
  return (degrees * Math.PI) / 180;
}

/**
 * Calculate bearing between two points
 * @param {Object} point1 - { lat, lng }
 * @param {Object} point2 - { lat, lng }
 * @returns {number} Bearing in degrees (0-360)
 */
export function calculateBearing(point1, point2) {
  const dLon = toRadians(point2.lng - point1.lng);
  const lat1 = toRadians(point1.lat);
  const lat2 = toRadians(point2.lat);

  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);

  const bearing = Math.atan2(y, x);
  return (toDegrees(bearing) + 360) % 360;
}

/**
 * Convert radians to degrees
 * @private
 * @param {number} radians
 * @returns {number} Degrees
 */
function toDegrees(radians) {
  return (radians * 180) / Math.PI;
}

/**
 * Check if a point is within a radius of another point
 * @param {Object} center - { lat, lng }
 * @param {Object} point - { lat, lng }
 * @param {number} radiusKm - Radius in kilometers
 * @returns {boolean}
 */
export function isWithinRadius(center, point, radiusKm) {
  const distance = calculateDistance(center, point);
  return distance <= radiusKm;
}
